import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Button,
  LinearProgress,
} from "@mui/material";
import PhotoIcon from "@mui/icons-material/Photo";
import DescriptionIcon from "@mui/icons-material/Description";
import VideocamIcon from "@mui/icons-material/Videocam";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { lightTheme } from "../../styles/theme";
import { NOTICE_VIDEO_INPUT_ACCEPT } from "../../constants/uploadLimits";
import type { NoticeUploadProgressState } from "../../utils/presignedUpload";
import { formatNoticeUploadStatus } from "../../utils/presignedUpload";
import { NoticeComposerAttachmentThumb } from "./NoticeComposerAttachmentThumb";
import { isDriveVideoFile } from "../../utils/driveFolder";

export type MobileUploadedFile = {
  fileUrl: string;
  fileName: string;
  contentType: string;
  size: number;
};

const TILE_APPEAR = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.28, ease: "easeOut" as const },
};

const SECTION_ENTER = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
};

const getFileExtension = (fileName: string) => {
  const parts = fileName.split(".");
  if (parts.length < 2) return "FILE";
  return parts[parts.length - 1].toUpperCase();
};

export interface MobileNoticeAttachmentSlotsProps {
  /** When embedded in another composer, skip staged motion timings. */
  embedded?: boolean;
  mode?: "attachment" | "image";
  uploadedFiles?: MobileUploadedFile[];
  errors: Record<string, string | undefined>;
  isUploading?: boolean;
  uploadStatusMessage?: string;
  uploadProgress?: NoticeUploadProgressState | null;
  onCancelUpload?: () => void;
  handleFileUpload?: (files: FileList) => Promise<void>;
  handleFileRemove?: (index: number) => void;
}

/**
 * Shared mobile attachment UX: Photo / Document / Video pickers plus thumbnail grid,
 * reused by MobileAttachmentComposer and MobileInfoNoticeComposer.
 */
export const MobileNoticeAttachmentSlots: React.FC<
  MobileNoticeAttachmentSlotsProps
> = ({
  embedded = false,
  mode = "attachment",
  uploadedFiles: uploadedProp,
  errors,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
  handleFileUpload,
  handleFileRemove,
}) => {
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const attachmentsInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [pendingUploads, setPendingUploads] = useState<
    Array<{ id: string; fileName: string; contentType: string }>
  >([]);
  const maxAttachments = 20;
  const uploadedFiles = uploadedProp ?? [];
  const canAddMore = uploadedFiles.length < maxAttachments;
  const isImageMode = mode === "image";

  const triggerUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (!e.target.files || !e.target.files.length) return;
    const selectedFiles = Array.from(e.target.files);
    setPendingUploads(
      selectedFiles.map((file, index) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${index}`,
        fileName: file.name,
        contentType: file.type,
      })),
    );
    if (handleFileUpload) {
      try {
        await handleFileUpload(e.target.files);
      } finally {
        setPendingUploads([]);
      }
    }
    e.target.value = "";
  };

  return (
    <>
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple={!isImageMode}
        style={{ display: "none" }}
        onChange={triggerUpload}
      />
      <input
        ref={attachmentsInputRef}
        type="file"
        accept={`image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx,.csv,${NOTICE_VIDEO_INPUT_ACCEPT}`}
        multiple
        style={{ display: "none" }}
        onChange={triggerUpload}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={triggerUpload}
      />
      <input
        ref={documentInputRef}
        type="file"
        accept=".pdf,.txt,.doc,.docx,.xls,.xlsx,.csv"
        multiple
        style={{ display: "none" }}
        onChange={triggerUpload}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept={NOTICE_VIDEO_INPUT_ACCEPT}
        multiple
        style={{ display: "none" }}
        onChange={triggerUpload}
      />

      {!isImageMode && (
        <Stack
          component={motion.div}
          {...(!embedded
            ? {
                initial: SECTION_ENTER.initial,
                animate: SECTION_ENTER.animate,
                transition: { duration: 0.34, ease: "easeOut", delay: 0.23 },
              }
            : { initial: false })}
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.85,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Add attachments
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {uploadedFiles.length}/{maxAttachments}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            {(
              [
                {
                  id: "photos",
                  label: "Photo",
                  icon: PhotoIcon,
                  onClick: () => galleryInputRef.current?.click(),
                },
                {
                  id: "document",
                  label: "Document",
                  icon: DescriptionIcon,
                  onClick: () => documentInputRef.current?.click(),
                },
                {
                  id: "video",
                  label: "Video",
                  icon: VideocamIcon,
                  onClick: () => videoInputRef.current?.click(),
                },
              ] as const
            ).map((action) => {
              const IconComponent = action.icon;
              const disabled = !canAddMore || !!isUploading;
              return (
                <Box
                  key={action.id}
                  onClick={() => {
                    if (!disabled) action.onClick();
                  }}
                  sx={{
                    position: "relative",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    border: "1px dashed",
                    borderColor: disabled ? "grey.300" : "grey.400",
                    backgroundColor: "grey.50",
                    aspectRatio: "1 / 1",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    px: 0.75,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    "&:active": {
                      transform: disabled ? "none" : "scale(0.98)",
                    },
                    transition:
                      "transform 0.14s ease, border-color 0.18s ease, opacity 0.18s ease",
                  }}
                >
                  <IconComponent
                    sx={{ color: "text.secondary", fontSize: 34 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {action.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Stack>
      )}

      {!!errors.attachmentUrl &&
        uploadedFiles.length === 0 &&
        pendingUploads.length === 0 && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 0.5 }}
          >
            {errors.attachmentUrl}
          </Typography>
        )}

      {(isImageMode ||
        uploadedFiles.length > 0 ||
        pendingUploads.length > 0) && (
        <Box
          component={motion.div}
          {...(!embedded
            ? {
                initial: SECTION_ENTER.initial,
                animate: SECTION_ENTER.animate,
                transition: {
                  duration: 0.34,
                  ease: "easeOut",
                  delay: 0.29,
                },
              }
            : { initial: false })}
          sx={{ mt: isImageMode && !embedded ? 1 : embedded ? 0.5 : 1, mb: 2 }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            {canAddMore && (
              <Box
                onClick={() => {
                  if (canAddMore && !isUploading) {
                    if (isImageMode) {
                      galleryInputRef.current?.click();
                    } else {
                      attachmentsInputRef.current?.click();
                    }
                  }
                }}
                sx={{
                  position: "relative",
                  borderRadius: 1.5,
                  overflow: "hidden",
                  border: "1px dashed",
                  borderColor: "grey.400",
                  backgroundColor: "grey.50",
                  aspectRatio: "1 / 1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  cursor: canAddMore && !isUploading ? "pointer" : "default",
                }}
              >
                <PhotoIcon sx={{ color: "text.secondary", fontSize: 34 }} />
                {isImageMode && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      textAlign: "center",
                      lineHeight: 1.2,
                      maxWidth: 88,
                    }}
                  >
                    Tap to add image
                  </Typography>
                )}
              </Box>
            )}
            {uploadedFiles.map((file, originalIndex) => (
              <Box
                key={`${file.fileUrl}-${originalIndex}`}
                component={motion.div}
                initial={TILE_APPEAR.initial}
                animate={TILE_APPEAR.animate}
                transition={TILE_APPEAR.transition}
                sx={{
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "grey.200",
                    backgroundColor: "grey.100",
                    aspectRatio: "1 / 1",
                  }}
                >
                  {file.contentType?.startsWith("image/") ||
                  isDriveVideoFile(file.contentType, file.fileName) ? (
                    <NoticeComposerAttachmentThumb
                      file={file}
                      variant="tile"
                      showPlayHint={false}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.6,
                        backgroundColor: "grey.50",
                      }}
                    >
                      <DescriptionIcon
                        sx={{
                          color: lightTheme.colors.materialBlue,
                          fontSize: 36,
                        }}
                      />
                      <Box
                        sx={{
                          px: 0.8,
                          py: 0.15,
                          borderRadius: 0.8,
                          backgroundColor: lightTheme.colors.materialBlue,
                          color: "common.white",
                          fontSize: "0.62rem",
                          lineHeight: 1.2,
                          fontWeight: 700,
                          maxWidth: "70%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getFileExtension(file.fileName)}
                      </Box>
                    </Box>
                  )}
                  {handleFileRemove && (
                    <IconButton
                      size="small"
                      onClick={() => handleFileRemove(originalIndex)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 24,
                        height: 24,
                        backgroundColor: "rgba(0,0,0,0.55)",
                        color: "common.white",
                        transform: "scale(1)",
                        transition:
                          "transform 0.14s ease, background-color 0.18s ease",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                        "&:active": { transform: "scale(0.92)" },
                      }}
                    >
                      <CloseIcon
                        sx={{
                          fontSize: (th) => th.typography.body2.fontSize,
                        }}
                      />
                    </IconButton>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    px: 0.25,
                    display: "block",
                    fontSize: "0.72rem",
                    lineHeight: 1.25,
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={file.fileName}
                >
                  {file.fileName}
                </Typography>
              </Box>
            ))}
            {pendingUploads.map((file) => (
              <Box
                key={file.id}
                component={motion.div}
                initial={TILE_APPEAR.initial}
                animate={TILE_APPEAR.animate}
                transition={TILE_APPEAR.transition}
                sx={{
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    border: "1px dashed",
                    borderColor: "primary.main",
                    backgroundColor: "rgba(25, 118, 210, 0.06)",
                    aspectRatio: "1 / 1",
                    animation: "pendingPulse 1.6s ease-in-out infinite",
                    "@keyframes pendingPulse": {
                      "0%, 100%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.012)" },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    {file.contentType?.startsWith("image/") ? (
                      <PhotoIcon sx={{ color: "primary.main", fontSize: 34 }} />
                    ) : (
                      <DescriptionIcon
                        sx={{
                          color: lightTheme.colors.materialBlue,
                          fontSize: 34,
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: "rgba(0,0,0,0.55)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.6,
                      px: 1,
                      color: "common.white",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.62rem",
                        textAlign: "center",
                        lineHeight: 1.2,
                      }}
                    >
                      {uploadProgress
                        ? formatNoticeUploadStatus(
                            uploadProgress.fileName,
                            uploadProgress.percent,
                            uploadProgress.fileIndex,
                            uploadProgress.fileCount
                          )
                        : uploadStatusMessage || "Uploading…"}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress?.percent ?? 0}
                      sx={{
                        width: "88%",
                        height: 5,
                        borderRadius: 3,
                        bgcolor: "rgba(255,255,255,0.25)",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "common.white",
                        },
                      }}
                    />
                    {onCancelUpload ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancelUpload();
                        }}
                        sx={{
                          minWidth: 0,
                          py: 0,
                          px: 0.5,
                          color: "common.white",
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          textTransform: "none",
                        }}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    px: 0.25,
                    display: "block",
                    fontSize: "0.72rem",
                    lineHeight: 1.25,
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={file.fileName}
                >
                  {file.fileName}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default MobileNoticeAttachmentSlots;
